import { assign, fromPromise, setup } from "xstate";
import { DEFAULT_LIST_COLOR_HEX, DEFAULT_LIST_ICON } from "../lists/constants";

export interface ListFormInput {
  listId?: string;
  groupId?: string;
  initialData?: {
    name: string;
    color: string;
    icon: string;
    themeId: string | null;
    isBlur: boolean;
  };
}

export interface ListFormContext {
  listId?: string;
  groupId?: string;
  name: string;
  color: string;
  icon: string;
  themeId: string | null;
  isBlur: boolean;
  originalThemeId: string | null;
  error: string | null;
}

export type ListFormEvent =
  | { type: "NAME_CHANGED"; name: string }
  | { type: "COLOR_CHANGED"; color: string }
  | { type: "ICON_CHANGED"; icon: string }
  | { type: "THEME_CHANGED"; themeId: string | null }
  | { type: "BLUR_CHANGED"; isBlur: boolean }
  | { type: "SAVE" }
  | { type: "CANCEL" }
  | { type: "SYNC_DATA"; data: Partial<ListFormContext> };

const DEFAULT_COLOR = DEFAULT_LIST_COLOR_HEX;
const DEFAULT_ICON = DEFAULT_LIST_ICON;

export const listFormMachine = setup({
  // Schema Definition Pattern for XState v5 type inference
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  types: {} as {
    context: ListFormContext;
    events: ListFormEvent;
    input: ListFormInput;
  },
  actors: {
    saveListActor: fromPromise(
      // oxlint-disable-next-line typescript/require-await
      async (_x: {
        input: {
          id?: string;
          groupId?: string;
          values: {
            name: string;
            color: string;
            icon: string;
            themeId: string | null;
            isBlur: boolean;
          };
        };
      }) => {
        // Placeholder for override in implementation
      },
    ),
    updateThemePreviewActor: fromPromise(
      // oxlint-disable-next-line typescript/require-await
      async (_x: { input: { id: string; themeId: string | null } }) => {
        // Placeholder for override in implementation
      },
    ),
  },
  actions: {
    updateName: assign({
      name: ({ event }) => (event.type === "NAME_CHANGED" ? event.name : ""),
    }),
    updateColor: assign({
      color: ({ event }) => (event.type === "COLOR_CHANGED" ? event.color : ""),
    }),
    updateIcon: assign({
      icon: ({ event }) => (event.type === "ICON_CHANGED" ? event.icon : ""),
    }),
    updateTheme: assign({
      themeId: ({ event }) => (event.type === "THEME_CHANGED" ? event.themeId : null),
    }),
    updateBlur: assign({
      isBlur: ({ event }) => (event.type === "BLUR_CHANGED" ? event.isBlur : true),
    }),
    syncData: assign(({ context, event }) => {
      if (event.type !== "SYNC_DATA") {
        return {};
      }
      return {
        ...event.data,
        originalThemeId:
          event.data.themeId === undefined ? context.originalThemeId : event.data.themeId,
      };
    }),
  },
}).createMachine({
  id: "listForm",
  initial: "idle",
  context: ({ input }) => ({
    listId: input.listId,
    groupId: input.groupId,
    name: input.initialData?.name ?? "",
    color: input.initialData?.color ?? DEFAULT_COLOR,
    icon: input.initialData?.icon ?? DEFAULT_ICON,
    themeId: input.initialData?.themeId ?? null,
    isBlur: input.initialData?.isBlur ?? true,
    originalThemeId: input.initialData?.themeId ?? null,
    error: null,
  }),
  states: {
    idle: {
      on: {
        NAME_CHANGED: {
          actions: "updateName",
        },
        COLOR_CHANGED: {
          actions: "updateColor",
        },
        ICON_CHANGED: {
          actions: "updateIcon",
        },
        THEME_CHANGED: {
          target: "previewingTheme",
          actions: "updateTheme",
        },
        BLUR_CHANGED: {
          actions: "updateBlur",
        },
        SYNC_DATA: {
          actions: "syncData",
        },
        SAVE: {
          target: "saving",
        },
        CANCEL: {
          target: "cancelling",
        },
      },
    },
    previewingTheme: {
      invoke: {
        src: "updateThemePreviewActor",
        input: ({ context }) => ({
          id: context.listId ?? "",
          themeId: context.themeId,
        }),
        onDone: "idle",
        onError: "idle", // Ignore errors for preview
      },
      on: {
        NAME_CHANGED: { actions: "updateName" },
        COLOR_CHANGED: { actions: "updateColor" },
        ICON_CHANGED: { actions: "updateIcon" },
        THEME_CHANGED: {
          target: "previewingTheme",
          actions: "updateTheme",
          reenter: true,
        },
        BLUR_CHANGED: { actions: "updateBlur" },
        SAVE: "saving",
        CANCEL: "cancelling",
      },
    },
    saving: {
      invoke: {
        src: "saveListActor",
        input: ({ context }) => ({
          id: context.listId,
          groupId: context.groupId,
          values: {
            name: context.name,
            color: context.color,
            icon: context.icon,
            themeId: context.themeId,
            isBlur: context.isBlur,
          },
        }),
        onDone: "success",
        onError: {
          target: "idle",
          actions: assign({
            error: ({ event }) =>
              event.error instanceof Error ? event.error.message : String(event.error),
          }),
        },
      },
    },
    cancelling: {
      invoke: {
        src: "updateThemePreviewActor",
        input: ({ context }) => ({
          id: context.listId ?? "",
          themeId: context.originalThemeId,
        }),
        onDone: "success",
        onError: "success", // Proceed even if revert fails
      },
    },
    success: {
      type: "final",
    },
  },
});
